<template>
  <ModalVue :modalActive="showModal" @close="close" :hasHeaderBorder="true" size="large">
    <template #header>
      <div class="flex flex-col text-left">
        <span class="font-bold text-2xl text-textPrimary">{{ modalTitle }}</span>
      </div>
    </template>

    <div class="p-4">
      <ManagementDoctorTable :headers="headers" :items="displayedItems" />
    </div>

    <!-- Contadores com as mesmas cores dos chips da prévia; contador zerado não aparece. -->
    <div class="flex flex-row flex-wrap justify-center items-center gap-2 px-4">
      <span v-if="conflictCount" class="chip chip-conflict" data-testid="expanded-conflict-count">
        {{ conflictShiftsText }}
      </span>
      <span v-if="openCount" class="chip chip-open" data-testid="expanded-open-count">
        {{ openShiftsText }}
      </span>
      <span v-if="closedCount" class="chip chip-doctor" data-testid="expanded-closed-count">
        {{ closedShiftsText }}
      </span>
      <span
        v-if="unidentifiedCount"
        class="chip chip-unidentified"
        data-testid="expanded-unidentified-count"
      >
        {{ unidentifiedShiftsText }}
      </span>
      <span v-if="blockedCount" class="chip chip-blocked" data-testid="expanded-blocked-count">
        <svg class="chip-icon" viewBox="0 0 24 24" aria-hidden="true"><path :d="mdiLock" /></svg>
        {{ blockedShiftsText }}
      </span>
      <span class="chip chip-total" data-testid="expanded-total-count">{{ totalShiftsText }}</span>
      <button
        v-if="displayedItems.length < items.length"
        @click="showMore"
        class="text-primary30 font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Ver mais
      </button>
    </div>

    <template #footer>
      <div class="flex justify-end p-4">
        <FilledButton height="48px" width="100px" @click="close" class="bg-primary30"
          >Fechar</FilledButton
        >
      </div>
    </template>
  </ModalVue>
</template>

<script lang="ts" setup>
import { ref, computed, PropType, watch } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import ManagementDoctorTable from '../components/ManagementDoctorTable.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { mdiLock } from '@mdi/js'

interface TableHeader {
  key: string
  label: string
}

type ModalMode = 'remove' | 'allocate' | 'change'

const props = defineProps({
  showModal: {
    type: Boolean,
    required: true
  },
  headers: {
    type: Array as PropType<TableHeader[]>,
    required: true
  },
  items: {
    type: Array as PropType<any[]>,
    required: true
  },
  mode: {
    type: String as PropType<ModalMode>,
    required: true,
    validator: (value: string) => ['remove', 'allocate', 'change'].includes(value)
  },
  conflictCount: {
    type: Number,
    required: true
  },
  openCount: {
    type: Number,
    required: true
  },
  closedCount: {
    type: Number,
    required: true
  },
  totalCount: {
    type: Number,
    required: true
  },
  unidentifiedCount: { type: Number as PropType<number | null>, default: null },
  // Título do modal de origem (ex.: "Editar responsável"); sem ele, cai no título do modo.
  title: { type: String as PropType<string | null>, default: null },
  blockedCount: { type: Number as PropType<number | null>, default: null }
})

const emit = defineEmits(['close'])

const itemsToShow = 15
const shiftsShownCount = ref(itemsToShow)

const displayedItems = computed(() => {
  return props.items.slice(0, shiftsShownCount.value)
})

const titles: Record<ModalMode, string> = {
  remove: 'Remover médico',
  allocate: 'Alocar médico',
  change: 'Alterar médico'
}

const modalTitle = computed(() => props.title || titles[props.mode])

const conflictShiftsText = computed(() => {
  const plural = props.conflictCount !== 1
  return `+${props.conflictCount} plant${plural ? 'ões' : 'ão'} com conflit${plural ? 'os' : 'o'}`
})

const openShiftsText = computed(() => {
  const plural = props.openCount !== 1
  return `+${props.openCount} plant${plural ? 'ões' : 'ão'} abert${plural ? 'os' : 'o'}`
})

const closedShiftsText = computed(() => {
  const plural = props.closedCount !== 1
  return `+${props.closedCount} plant${plural ? 'ões' : 'ão'} com médico`
})

const unidentifiedShiftsText = computed(() => {
  const n = props.unidentifiedCount ?? 0
  return `+${n} plant${n !== 1 ? 'ões' : 'ão'} ocupad${n !== 1 ? 'os' : 'o'} s/ ident.`
})

const blockedShiftsText = computed(() => {
  const n = props.blockedCount ?? 0
  return `+${n} plant${n !== 1 ? 'ões' : 'ão'} bloquead${n !== 1 ? 'os' : 'o'}`
})

const totalShiftsText = computed(() => {
  const plural = props.totalCount !== 1
  return `+${props.totalCount} plant${plural ? 'ões' : 'ão'}`
})

function showMore() {
  shiftsShownCount.value += itemsToShow
}

function close() {
  emit('close')
}

watch(
  () => props.items,
  () => {
    shiftsShownCount.value = itemsToShow
  }
)
</script>

<style scoped>
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  border: 1px solid;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.chip-icon {
  width: 14px;
  height: 14px;
  fill: currentColor;
}
.chip-conflict {
  background: #da1e28;
  border-color: #da1e28;
  color: #fff;
}
.chip-open {
  background: #fef3e7;
  border-color: #f18f1b;
  color: #8a4b00;
}
.chip-doctor {
  background: #e0f2f6;
  border-color: #84c8d7;
  color: #235e6c;
}
.chip-unidentified {
  background: repeating-linear-gradient(135deg, #eeeef3 0 5px, #e1e1ea 5px 10px);
  border-color: #bcbccd;
  color: #3e3e5a;
}
.chip-blocked {
  background: #fdecec;
  border-color: #f3a5a9;
  color: #da1e28;
}
.chip-total {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #374151;
}
</style>
